import React, { useState, useRef, useEffect, useCallback } from 'react'
import { main } from './backend/getPageData'
// ─── TYPES ────────────────────────────────────────────────────────────────────
type GameType = 'slots' | 'blackjack' | 'roulette' | 'poker' | 'bingo' | 'generic'

interface GameItem {
  id: number
  name: string
  type: string
  tag: string
  emoji: string
  desc: string
  rtp: string
  features: string[]
  hot: boolean
  gameType: GameType
}

interface FeatureBox { icon: string; title: string; body: string }
interface FaqItem { q: string; a: string }
interface ModalData { icon: string; title: string; html: string }

interface PageContent {
  topBanner: string
  heroTitle: string
  heroSub: string
  heroBadge: string
  disclaimer: string
  games: GameItem[]
  features: FeatureBox[]
  faq: FaqItem[]
  modals: Record<string, ModalData>
  helpLine: string
  helperOrg: string
}

// ─── GCLID ────────────────────────────────────────────────────────────────────
function getGclid(): string {
  try {
    let s = window.location.search
    try { if (window.parent && window.parent !== window) s = window.parent.location.search } catch {}
    return new URLSearchParams(s).get('gclid') || ''
  } catch { return '' }
}

// ─── DEFAULT DATA ─────────────────────────────────────────────────────────────
const DEFAULT: PageContent = {
  topBanner: '🎮 Pelaa maksutta · Ei rahaa · Ei palkintoja · Vain viihdettä · 18+',
  heroTitle: 'Sinisen aallon\nsosiaalipelit.',
  heroSub: 'Quinoro on suomenkielinen sosiaalinen kasinostudio. Rauhallinen käyttöliittymä, omat pelimme ja nolla rahapanosta — kaikki pisteet ovat vain viihdearvoa varten.',
  heroBadge: '✦ Quinoro — rauhallista sosiaalipeliä, ei rahapelaamista',
  disclaimer: '⚠️ Tämä sivusto tarjoaa vain Quinoron omia sosiaalisia kasinopelejä viihdetarkoituksessa. Täällä ei pelata oikealla rahalla eikä voi voittaa rahallisia, tavara- tai sweepstake-palkintoja. Kaikki pelit ovat täysin ilmaisia, eikä sivusto sisällä sovelluksen sisäisiä ostoja. Quinoro ei ole aggregaattori eikä affiliate-sivusto — emme linkitä, suosittele tai markkinoi kolmansien osapuolten rahapeli- tai sosiaalisen kasinon palveluja. Sosiaalinen kasinopelaaminen ei vastaa oikeaa rahapelausta. Ikäraja: 18+. Jos sinulla on ongelmia rahapelaamisessa, soita Peluuri-auttavalle puhelimelle: 0800 100 101 (maksuton, auki arkisin 12–18).',
  helpLine: '0800 100 101',
  helperOrg: 'Peluuri',
  games: [
    { id: 1, name: 'Sapphire Spins', type: 'Kolikkopelit', tag: 'SUOSITTU', emoji: '🎰', gameType: 'slots',
      desc: 'Viisi kelaa, 15 voittolinjaa ja safiirin siniset symbolit. Quinoron oma kolikkopeli, jonka bonuskierrokset tuovat vain virtuaalipisteitä — ei rahaa, ei arpajaispalkintoja, ei ostoja.',
      rtp: 'Virtuaalinen 96.1%', features: ['Vapaakierrokset', 'Safiiri-Wild', 'Bonusround', 'Virtuaalipisteet'], hot: true },
    { id: 2, name: "Harbor Hold'em", type: 'Pokeri', tag: 'UUSI', emoji: '🂡', gameType: 'poker',
      desc: "Texas Hold'em rauhallisessa satamaympäristössä. Pelaa tekoälyä vastaan tai liity yhteisöpöytään. Kaikki panokset ovat maksuttomia virtuaalimerkkejä — ei oikeaa rahaa, ei riskiä.",
      rtp: 'Virtuaalinen 96.9%', features: ['Yhteisöpöydät', 'Päiväturnaukset', 'Tekoälyvastustajat', 'Käsihistoria'], hot: false },
    { id: 3, name: 'Pacific Roulette', type: 'Ruletti', tag: 'KLASSIKKO', emoji: '🎡', gameType: 'roulette',
      desc: 'Eurooppalainen ruletti rannikkoteemalla. Sisä- ja ulkovedot kokonaan virtuaalipisteillä. Voitot näkyvät vain näytöllä — niitä ei voi vaihtaa rahaksi eikä palkinnoiksi.',
      rtp: 'Virtuaalinen 97.1%', features: ['Eurooppalainen pyörä', 'Quick spin', 'Tilastohistoria', 'Automaattiveto'], hot: false },
    { id: 4, name: 'Marine Blackjack', type: 'Blackjack', tag: 'SUOSITTU', emoji: '🂫', gameType: 'blackjack',
      desc: 'Klassinen 21 merenrannan pöytäkuvalla. Todennäköisyysvihje ja perusstrategiaopas aina näkyvillä. Pelimerkit ovat virtuaalisia eikä niillä ole rahallista arvoa.',
      rtp: 'Virtuaalinen 99.2%', features: ['Strategiaopas', 'Todennäköisyysvihje', 'Kaksoistus', 'Split'], hot: true },
    { id: 5, name: 'Tidal Jackpot', type: 'Kolikkopelit', tag: 'JACKPOT', emoji: '💎', gameType: 'slots',
      desc: 'Aaltoileva yhteisöjackpot, joka kasvaa ilmaisten pyöräytysten myötä. Jackpot-pisteet ovat puhtaasti leaderboard-arvoa — niitä ei voi lunastaa rahaksi tai palkinnoiksi.',
      rtp: 'Virtuaalinen 95.6%', features: ['Yhteisö-jackpot', 'Päivittäinen kasvu', 'Mega Wild', 'Free spins'], hot: true },
    { id: 6, name: 'Sunset Bingo', type: 'Bingo', tag: 'YHTEISÖ', emoji: '🌸', gameType: 'bingo',
      desc: 'Auringonlaskutunnelma bingosalissa. Sadat pelaajat samassa huoneessa, chat täynnä iloa. Osallistuminen on aina maksutonta eikä palkintoja jaeta — vain pisteet näytölle.',
      rtp: 'Virtuaalinen 95.3%', features: ['Live-chat', 'Viikkoteemat', 'Rivi/Täysi-kortti', 'Ilmaisliput'], hot: false },
    { id: 7, name: 'Ocean Symbols', type: 'Kolikkopelit', tag: 'UUSI', emoji: '🔵', gameType: 'slots',
      desc: 'Kuusi kelaa ja cascading wins -mekaniikka valtameriteemalla. Jokaisella symbolilla oma virtuaaliarvonsa — mikään pistemäärä ei muutu oikeaksi rahaksi tai palkinnoksi.',
      rtp: 'Virtuaalinen 96.7%', features: ['Cascading Wins', '6 kelaa', 'Helmibonus', 'Scatter Wild'], hot: false },
    { id: 8, name: 'Quinoro Studio', type: 'Studio-pelit', tag: 'PREMIUM', emoji: '🎴', gameType: 'blackjack',
      desc: 'Studio-sarja blackjackia, rulettia ja baccaratia animoidulla jakajagrafiikalla. Suomenkielinen käyttöliittymä — ei oikeaa jakajaa, ei oikeaa rahaa, pelkkää viihdettä.',
      rtp: 'Virtuaalinen 97.5%', features: ['Animoitu jakaja', 'HD-grafiikka', 'Suomenkielinen chat', 'Useita pöytiä'], hot: true },
  ],
  features: [
    { icon: '🆓', title: '100% ilmainen — aina', body: 'Kaikki pelit ovat täysin maksuttomia ilman piilomaksuja. Et tarvitse luottokorttia tai pankkitietoja. Virtuaalikolikot eivät maksa mitään eivätkä ole vaihdettavissa oikeaksi rahaksi.' },
    { icon: '🚫', title: 'Ei sweepstake-palkintoja', body: 'Quinorossa ei jaeta arpajaisia, tavarapalkintoja eikä lahjakortteja. Pelit eivät ole sweepstake-casinoja — voitetut pisteet jäävät tilillesi viihdettä varten.' },
    { icon: '🧭', title: 'Ei aggregaattori eikä affiliate', body: 'Quinoro tarjoaa vain omia sosiaalisia pelejä. Emme linkitä, suosittele emmekä ohjaa käyttäjiä ulkopuolisille rahapelisivustoille tai kasinooperaattoreille.' },
    { icon: '🔒', title: 'Turvallinen ja yksityinen', body: 'Sivusto käyttää TLS-salausta tiedonsiirrossa, emmekä myy tietojasi kolmansille osapuolille. GDPR-yhteensopiva käsittely kaikilta osin.' },
    { icon: '📱', title: 'Toimii kaikilla laitteilla', body: 'Selain riittää — ei latausta, ei asennusta. Optimoitu puhelimelle, tabletille ja tietokoneelle suomeksi.' },
    { icon: '👥', title: 'Kasvava yhteisö', body: 'Kymmeniätuhansia rekisteröityjä pelaajia. Pelaa ystävien kanssa ja kilpaile viikkotilastoissa — vain pisteillä, ei rahalla.' },
  ],
  faq: [
    { q: 'Voiko Quinorossa voittaa oikeaa rahaa?', a: 'Ei. Quinoro on sosiaalinen kasinoviihdesivusto, jossa pelataan yksinomaan virtuaalisilla pisteillä. Pisteitä ei voi vaihtaa rahaksi, lahjakortteihin tai muihin palkintoihin, eikä niillä voi hävitä oikeaa rahaa.' },
    { q: 'Onko Quinoro sweepstake-kasino?', a: 'Ei. Quinoro ei tarjoa sweepstake-pelejä eikä mitään palkintoja, arpajaisia tai tavarapalkintoja. Kaikki voitot ovat puhtaasti virtuaalisia pisteitä, joilla ei ole rahallista arvoa eikä lunastettavaa sisältöä.' },
    { q: 'Onko Quinoro aggregaattori tai affiliate?', a: 'Ei. Quinoro ei ole aggregaattori eikä affiliate-sivusto. Tarjoamme omia sosiaalisia pelejämme suoraan käyttäjille — emme linkitä, suosittele tai ohjaa pelaajia ulkopuolisille rahapelisivustoille tai operaattoreille.' },
    { q: 'Onko sivusto laillinen Suomessa?', a: 'Kyllä. Sosiaalinen pelaaminen ilman rahallisia palkintoja ei kuulu Suomen arpajaislain piiriin. Quinoro tarjoaa vain virtuaalipisteillä toimivia pelejä viihdetarkoituksessa 18-vuotiaille ja vanhemmille.' },
    { q: 'Pitääkö rekisteröityä pelatakseni?', a: 'Voit kokeilla useimpia pelejä ilman rekisteröitymistä vierailutilassa. Rekisteröitymällä saat pysyvän pelitilisi, päivittäiset bonuspisteet ja pääsyn turnauksiin. Rekisteröinti on ilmaista.' },
    { q: 'Miksi sivustolla on ikäraja 18+?', a: 'Google Adsin sosiaalisten kasinopelien mainostamispolitiikka edellyttää 18 vuoden ikärajaa. Noudatamme tätä vaatimusta ja suosittelemme vastuullista ajankäyttöä.' },
    { q: 'Mitä tehdä, jos pelaaminen tuntuu ongelmalliselta?', a: 'Jos sinulla on huoli omasta tai läheisesi pelaamisesta, Peluuri tarjoaa maksutonta apua numerossa 0800 100 101 (arkisin klo 12–18). Myös www.peluuri.fi tarjoaa tietoa ja tukea.' },
    { q: 'Sisältävätkö pelit sovelluksen sisäisiä ostoja?', a: 'Eivät. Quinoro on 100% ilmainen. Emme myy virtuaalipisteitä, bonuksia, skinejä tai maksullisia ominaisuuksia. Kaikki bonukset ansaitaan pelaamalla ja päivittäisellä kirjautumisella.' },
  ],
  modals: {
    tietoa: {
      icon: '✦',
      title: 'Tietoa Quinorosta',
      html: `
        <p>Quinoro on suomenkielinen sosiaalinen kasinoviihdealusta, jonka kaikki pelit ovat oman studiomme kehittämiä. Sivusto on suunniteltu tarjoamaan kasinotyylisten pelien viihdearvoa täysin ilmaiseksi ja ilman rahallista riskiä.</p>
        <div class="qo-box qo-box--warn">
          <strong>🚫 EI RAHAPELEJÄ</strong><br/>
          Quinoro ei ole rahapelioperaattori. Sivustolla ei pelata oikealla rahalla eikä tarjota rahallisia palkintoja. Kaikki pelit toimivat ainoastaan virtuaalipisteillä, joilla ei ole rahallista arvoa.
        </div>
        <div class="qo-box qo-box--warn">
          <strong>🎟️ EI SWEEPSTAKE-PALKINTOJA</strong><br/>
          Quinoro ei ole sweepstake-kasino. Emme jaa arpajaisia, tavarapalkintoja, lahjakortteja, kryptovaluuttaa tai mitään palkintoja, joilla voisi olla rahallista tai vaihdettavaa arvoa. Pelien tulokset ovat puhtaasti viihdepisteitä.
        </div>
        <div class="qo-box qo-box--warn">
          <strong>🧭 EI AGGREGAATTORI EIKÄ AFFILIATE-SIVUSTO</strong><br/>
          Quinoro ei ole aggregaattori eikä affiliate-sivusto. Tarjoamme omia sosiaalisia pelejämme suoraan käyttäjille. Emme linkitä, suosittele emmekä markkinoi kolmansien osapuolten rahapeli- tai sosiaalisia kasinopalveluja.
        </div>
        <div class="qo-box qo-box--warn">
          <strong>🛒 EI SOVELLUKSEN SISÄISIÄ OSTOJA</strong><br/>
          Tämä sivusto on 100% ilmainen. Quinoro ei myy virtuaalipisteitä eikä tarjoa maksullisia lisäominaisuuksia. Kaikki pelitoiminnot ovat täysin ilmaisia kaikille käyttäjille.
        </div>
        <h3>Palvelun tarkoitus</h3>
        <p>Sivusto on tarkoitettu aikuisviihteeksi. Pelit simuloivat kasinopelejä, mutta kaikki tulokset ovat virtuaalisia. Menestyminen sosiaalisen kasino -pelissä ei tarkoita tai ennusta menestymistä oikeissa rahapeleissä.</p>
        <h3>Yhteys</h3>
        <p>📧 palaute@quinoro.org | Asiakaspalvelu arkisin klo 9–17</p>
      `,
    },
    kayttoehto: {
      icon: '📄',
      title: 'Käyttöehdot',
      html: `
        <p><strong>Viimeksi päivitetty: 1. huhtikuuta 2026</strong></p>
        <div class="qo-box qo-box--warn">
          ⚠️ <strong>TÄRKEÄÄ:</strong> Nämä käyttöehdot sisältävät keskeisiä tietoja palvelun luonteesta. Lue huolellisesti ennen palvelun käyttöä.
        </div>

        <h3>1. Palvelun luonne ja rajoitukset</h3>
        <p>Quinoro on sosiaalinen pelialusta, joka tarjoaa kasinotyylisiä pelejä <strong>yksinomaan viihdetarkoituksessa</strong>. Palvelu ei ole rahapelipalvelu.</p>
        <ul>
          <li>Sivustolla käytettävillä virtuaalipisteillä <strong>ei ole rahallista arvoa</strong></li>
          <li>Virtuaalipisteitä <strong>ei voi vaihtaa rahaksi</strong>, lahjakortteihin, tavarapalkintoihin eikä muihin arvokkaisiin palkintoihin</li>
          <li>Sivusto <strong>ei tarjoa sovelluksen sisäisiä ostoja</strong> — kaikki toiminnot ovat ilmaisia</li>
          <li>Menestyminen näissä peleissä <strong>ei ennusta menestymistä</strong> oikeissa rahapeleissä</li>
          <li>Sivusto <strong>ei ole yhteydessä</strong> mihinkään rahapelioperaattoriin</li>
        </ul>

        <h3>2. Ei sweepstake-sisältöä</h3>
        <div class="qo-box qo-box--warn">
          🎟️ <strong>Quinoro ei ole sweepstake-kasino eikä sisällä sweepstake-tyyppistä sisältöä.</strong> Emme järjestä arpajaisia, emme jaa tavarapalkintoja, lahjakortteja, kryptovaluuttaa emmekä muita lunastettavissa olevia palkintoja. Kaikki pelien tulokset ovat puhtaasti virtuaalisia pisteitä, joilla ei ole rahallista tai vaihdannallista arvoa.
        </div>

        <h3>3. Ei aggregaattori eikä affiliate-sivusto</h3>
        <div class="qo-box qo-box--warn">
          🧭 <strong>Quinoro ei ole aggregaattori eikä affiliate-sivusto.</strong> Tarjoamme omia sosiaalisia pelejämme suoraan käyttäjille. Emme linkitä, suosittele, listaa emmekä markkinoi kolmansien osapuolten rahapeli-, sosiaalisen kasinon tai sweepstake-palveluita. Sivustolla ei ole affiliate- tai kumppanuuslinkkejä rahapelioperaattoreille.
        </div>

        <h3>4. Ikäraja — 18 vuotta</h3>
        <div class="qo-box qo-box--warn">
          🔞 <strong>Palvelun käyttö on sallittu vain 18 vuotta täyttäneille henkilöille.</strong> Alaikäisten käyttö on ehdottomasti kielletty. Epäilemme, että alaikäinen käyttää palvelua, poistamme tilin välittömästi.
        </div>

        <h3>5. Käyttäjätilit</h3>
        <ul>
          <li>Käyttäjä voi luoda vain yhden tilin</li>
          <li>Tiliä ei saa siirtää toiselle henkilölle</li>
          <li>Pidät tilin kirjautumistiedot salassa</li>
          <li>Ilmoitat välittömästi epäilyttävästä tilin käytöstä</li>
        </ul>

        <h3>6. Kielletty toiminta</h3>
        <ul>
          <li>Virtuaalipisteiden myyminen tai ostaminen kolmansien osapuolten kautta</li>
          <li>Automaattisten peliohjelmistojen tai bottien käyttö</li>
          <li>Palvelun häiritseminen tai hakkerointi</li>
          <li>Muiden käyttäjien häiritseminen</li>
        </ul>

        <h3>7. Tilin sulkeminen</h3>
        <p>Sinulla on oikeus sulkea tilisi milloin tahansa. Lähetä pyyntö osoitteeseen palaute@quinoro.org. Suljemme tilin 5 arkipäivän kuluessa ja poistamme henkilötietosi GDPR:n mukaisesti.</p>

        <h3>8. Muutokset käyttöehtoihin</h3>
        <p>Ilmoitamme merkittävistä muutoksista sähköpostitse 30 päivää etukäteen. Jatkamalla palvelun käyttöä muutosten jälkeen hyväksyt päivitetyt ehdot.</p>

        <h3>9. Sovellettava laki</h3>
        <p>Näihin käyttöehtoihin sovelletaan Suomen lakia. Mahdolliset riidat ratkaistaan Helsingin käräjäoikeudessa.</p>
      `,
    },
    tietosuoja: {
      icon: '🔒',
      title: 'Tietosuojaseloste (GDPR)',
      html: `
        <p><strong>Viimeksi päivitetty: 1. huhtikuuta 2026</strong></p>
        <p>Quinoro Oy käsittelee henkilötietoja EU:n yleisen tietosuoja-asetuksen (GDPR, 2016/679) ja Suomen tietosuojalain (1050/2018) mukaisesti.</p>

        <h3>1. Rekisterinpitäjä</h3>
        <p>Quinoro Oy<br/>Y-tunnus: 1234567-8<br/>Aleksanterinkatu 17, 00100 Helsinki<br/>📧 tietosuoja@quinoro.org</p>

        <h3>2. Käsiteltävät henkilötiedot</h3>
        <ul>
          <li><strong>Rekisteröintitiedot:</strong> sähköpostiosoite, syntymäaika (ikätarkistus)</li>
          <li><strong>Pelitilastot:</strong> pelitulokset, virtuaalikolikoiden saldo (anonymisoitu)</li>
          <li><strong>Tekniset tiedot:</strong> IP-osoite, selaintyyppi, käyttöaika</li>
          <li><strong>Viestintä:</strong> asiakaspalveluviestit</li>
        </ul>
        <div class="qo-box">
          ℹ️ <strong>Emme kerää maksutietoja.</strong> Quinoro on täysin ilmainen palvelu — maksutapahtumia ei ole.
        </div>

        <h3>3. Käsittelyn oikeusperusta</h3>
        <ul>
          <li>Sopimuksen täyttäminen (palvelun tarjoaminen)</li>
          <li>Oikeutettu etu (palvelun kehittäminen, turvallisuus)</li>
          <li>Suostumus (uutiskirje, markkinointi)</li>
          <li>Lakisääteinen velvoite (kirjanpito, ikärajan valvonta)</li>
        </ul>

        <h3>4. Tietojen säilytysaika</h3>
        <ul>
          <li>Aktiiviset käyttäjätilit: tilin sulkemiseen saakka</li>
          <li>Suljetut tilit: 30 päivää sulkemisesta</li>
          <li>Lokitiedot: 90 päivää</li>
          <li>Uutiskirjesuostumus: peruutukseen saakka</li>
        </ul>

        <h3>5. Tietojen luovutus</h3>
        <p>Emme myy henkilötietojasi kolmansille osapuolille. Käytämme seuraavia alihankkijoita:</p>
        <ul>
          <li>Google Analytics (analytiikka, anonymisoitu)</li>
          <li>Sähköpostipalveluntarjoaja (uutiskirje)</li>
          <li>Pilvipalveluntarjoaja (palvelimet, EU:ssa)</li>
        </ul>

        <h3>6. Sinun oikeutesi (GDPR 15–22 artiklat)</h3>
        <ul>
          <li><strong>Tarkastusoikeus:</strong> pyydä kopio tiedoistasi</li>
          <li><strong>Oikaiseminen:</strong> korjaa virheelliset tiedot</li>
          <li><strong>Poistaminen:</strong> "oikeus tulla unohdetuksi"</li>
          <li><strong>Käsittelyn rajoittaminen</strong></li>
          <li><strong>Siirto-oikeus:</strong> saat tietosi koneluettavassa muodossa</li>
          <li><strong>Vastusoikeus:</strong> vastusta tietojenkäsittelyä</li>
          <li><strong>Suostumuken peruutus:</strong> milloin tahansa</li>
        </ul>
        <div class="qo-box">
          📧 Lähetä tietopyyntösi osoitteeseen <strong>tietosuoja@quinoro.org</strong><br/>
          Vastaamme 30 päivän kuluessa.<br/>
          Voit myös tehdä valituksen <strong>Tietosuojavaltuutetulle:</strong> tietosuoja.fi
        </div>
      `,
    },
    evasteet: {
      icon: '🍪',
      title: 'Evästekäytäntö',
      html: `
        <p><strong>Viimeksi päivitetty: 1. huhtikuuta 2026</strong></p>
        <p>Käytämme evästeitä (cookies) parantaaksemme käyttökokemustasi. Tällä sivulla selitetään, mitä evästeitä käytämme ja miksi.</p>

        <h3>1. Välttämättömät evästeet (aina aktiiviset)</h3>
        <p>Nämä evästeet ovat välttämättömiä sivuston toiminnalle. Niitä ei voi poistaa käytöstä.</p>
        <ul>
          <li><strong>qo_session</strong> — Kirjautumisistunnon ylläpito (vanhenee 24h)</li>
          <li><strong>qo_age_verified</strong> — Ikätarkistuksen tulos (vanhenee 365 päivää)</li>
          <li><strong>qo_cookie_consent</strong> — Evästehyväksynnän tallennus</li>
          <li><strong>qo_prefs</strong> — Käyttäjän peliasetukset (kieliasetus, teema)</li>
        </ul>

        <h3>2. Analytiikkaevästeet (vaativat suostumuksen)</h3>
        <p>Käytämme Google Analytics -palvelua ymmärtääksemme, miten sivustoa käytetään. Kaikki IP-osoitteet anonymisoidaan.</p>
        <ul>
          <li><strong>_ga</strong> — Käyttäjän tunnistaminen eri vierailukerroilla (vanhenee 2 vuotta)</li>
          <li><strong>_gid</strong> — Käyttäjän tunnistaminen 24h sisällä</li>
          <li><strong>_gat</strong> — Pyyntöjen rajoittaminen</li>
        </ul>
        <div class="qo-box">
          ℹ️ Google Analytics on asetettu anonymisoimaan IP-osoitteet eikä se seuraa henkilökohtaisia tietoja.
        </div>

        <h3>3. Evästeiden hallinta</h3>
        <p>Voit hallita evästeitä:</p>
        <ul>
          <li>Klikkaamalla "Evästeasetukset" sivun alareunassa</li>
          <li>Selaimesi asetuksista (ohjeet alla)</li>
          <li>Lähettämällä viestiä osoitteeseen tietosuoja@quinoro.org</li>
        </ul>
        <p><strong>Selaimen ohjeet:</strong> Chrome: Asetukset → Tietosuoja → Evästeet | Firefox: Asetukset → Yksityisyys | Safari: Asetukset → Tietosuoja</p>
        <div class="qo-box">
          ⚠️ Välttämättömien evästeiden poistaminen voi häiritä sivuston toimintaa, kuten kirjautumista ja ikätarkistuksen säilyttämistä.
        </div>
      `,
    },
    vastuullinen: {
      icon: '🛡️',
      title: 'Vastuullinen pelaaminen',
      html: `
        <div class="qo-box qo-box--warn">
          🆘 <strong>Peluuri — Maksuton auttava puhelin</strong><br/>
          📞 <strong>0800 100 101</strong> — Maksuton, arkisin klo 12–18<br/>
          🌐 <a href="https://www.peluuri.fi" target="_blank">www.peluuri.fi</a><br/>
          💬 Chat-tuki myös verkossa arkisin
        </div>

        <h3>Sosiaalinen kasino vs. rahapelaaminen</h3>
        <p>Quinoro on <strong>sosiaalinen kasinoviihdesivusto</strong>, jossa ei pelata oikealla rahalla. Tämä tarkoittaa:</p>
        <ul>
          <li>✅ Et voi hävitä rahaa Quinorossa</li>
          <li>✅ Virtuaalipisteillä ei ole rahallista arvoa</li>
          <li>✅ Palvelu on 100% ilmainen — ei sovelluksen sisäisiä ostoja</li>
          <li>✅ Ei sweepstake-palkintoja, arpajaisia eikä tavarapalkintoja</li>
          <li>✅ Emme ole aggregaattori emmekä affiliate — emme linkitä rahapelisivustoille</li>
          <li>⚠️ Silti: pelaamiskäyttäytyminen voi heijastua laajempiin pelaamisvalintoihin</li>
        </ul>

        <h3>Merkkejä ongelmallisesta pelaamisesta</h3>
        <ul>
          <li>Pelaat enemmän kuin suunnittelet</li>
          <li>Pelaaminen häiritsee töitä, koulua tai ihmissuhteita</li>
          <li>Tunnet pakottavaa tarvetta pelata</li>
          <li>Peli on ainoa tapa rentoutua tai paeta ongelmia</li>
          <li>Olet huolissasi omasta tai läheisesi pelaamisesta</li>
        </ul>

        <h3>Omat rajoitustyökalut</h3>
        <p>Voit rajoittaa omaa pelaamistasi milloin tahansa:</p>
        <ul>
          <li><strong>Tauon pitäminen:</strong> Lähetä viesti palaute@quinoro.org ja pyydä tilin väliaikaista sulkemista</li>
          <li><strong>Pysyvä sulkeminen:</strong> Voit sulkea tilisi pysyvästi milloin tahansa</li>
          <li><strong>Selaimen estot:</strong> GamBlock, Betfilter — toimivat myös sosiaalisilla kasinoilla</li>
        </ul>

        <h3>Apua Suomessa</h3>
        <ul>
          <li>🇫🇮 <strong>Peluuri:</strong> 0800 100 101 (ark. 12–18, maksuton) | peluuri.fi</li>
          <li>🏥 <strong>THL rahapelihaitat:</strong> thl.fi/rahapelit</li>
          <li>👨‍⚕️ <strong>Peluurin chat:</strong> peluuri.fi (arkisin)</li>
          <li>🏛️ <strong>A-klinikka:</strong> a-klinikka.fi</li>
        </ul>

        <div class="qo-box">
          ℹ️ Pelaaminen on tarkoitettu viihteeksi. Jos pelaaminen ei tunnu enää hauskalta, on aika pitää tauko tai hakea apua. Avun pyytäminen on rohkeutta.
        </div>
      `,
    },
    yhteys: {
      icon: '✉️',
      title: 'Ota yhteyttä',
      html: `
        <p>Vastaamme kaikkiin viesteihin yleensä saman arkipäivän aikana.</p>
        <div class="qo-contacts">
          <div><span>📧</span><p>Yleinen palaute</p><p>palaute@quinoro.org</p></div>
          <div><span>🔒</span><p>Tietosuoja-asiat</p><p>tietosuoja@quinoro.org</p></div>
          <div><span>⚖️</span><p>Juridiset asiat</p><p>legal@quinoro.org</p></div>
          <div><span>🕘</span><p>Asiakaspalvelu</p><p>Ark. 9–17 (EET)</p></div>
        </div>
        <h3>Postiosoite</h3>
        <p>Quinoro Oy<br/>Aleksanterinkatu 17<br/>00100 Helsinki, Suomi</p>
        <div class="qo-box qo-box--warn">
          🆘 <strong>Rahapeliongelmissa älä odota — soita heti:</strong><br/>
          Peluuri <strong>0800 100 101</strong> (maksuton, arkisin 12–18)
        </div>
      `,
    },
  },
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAYABLE GAME ENGINES
// ═══════════════════════════════════════════════════════════════════════════════

// ─── SLOT MACHINE ─────────────────────────────────────────────────────────────
const SLOT_SYMBOLS = ['🍒', '🍋', '🔔', '💎', '⭐', '🎰', '🌟', '🔵', '🌊', '⚓']
const SLOT_PAYOUTS: Record<string, number> = {
  '🍒🍒🍒': 5, '🍋🍋🍋': 8, '🔔🔔🔔': 12, '💎💎💎': 50,
  '⭐⭐⭐': 25, '🎰🎰🎰': 100, '🌟🌟🌟': 30, '🔵🔵🔵': 20, '🌊🌊🌊': 15, '⚓⚓⚓': 10,
}

function SlotGame() {
  const [reels, setReels] = useState([['🎰','🎰','🎰'],['🎰','🎰','🎰'],['🎰','🎰','🎰']])
  const [coins, setCoins] = useState(500)
  const [bet, setBet] = useState(10)
  const [spinning, setSpinning] = useState(false)
  const [msg, setMsg] = useState('Paina PYÖRITÄ aloittaaksesi!')
  const [win, setWin] = useState(0)
  const [spinAnim, setSpinAnim] = useState([false,false,false])

  function spin() {
    if (spinning || coins < bet) return
    setCoins(c => c - bet)
    setWin(0)
    setMsg('Pyörii...')
    setSpinning(true)
    setSpinAnim([true,true,true])

    const results = [0,1,2].map(() => Array.from({length:3},()=>SLOT_SYMBOLS[Math.floor(Math.random()*SLOT_SYMBOLS.length)]))

    setTimeout(() => setSpinAnim([false,true,true]), 400)
    setTimeout(() => setSpinAnim([false,false,true]), 700)
    setTimeout(() => {
      setSpinAnim([false,false,false])
      setReels(results)
      const midRow = results.map(r=>r[1]).join('')
      const payout = SLOT_PAYOUTS[midRow] || 0
      const total = payout * bet
      if (payout > 0) {
        setCoins(c => c + total)
        setWin(total)
        setMsg(`🎉 Voitto! +${total} kolikkoa!`)
      } else {
        // partial matches
        const mid = results.map(r=>r[1])
        const pairs = mid[0]===mid[1]||mid[1]===mid[2]||mid[0]===mid[2]
        if (pairs) { const small = Math.floor(bet*0.5); setCoins(c=>c+small); setMsg(`🍀 Pieni voitto! +${small}`); setWin(small) }
        else setMsg('Ei voittoa. Yritä uudelleen!')
      }
      setSpinning(false)
    }, 1000)
  }

  return (
    <div className="qo-game-engine qo-slots-engine">
      <div className="qo-slots-coins">
        <span>💰 {coins} kolikkoa</span>
        <span>Panos: <button onClick={()=>setBet(b=>Math.max(5,b-5))} disabled={spinning}>−</button> {bet} <button onClick={()=>setBet(b=>Math.min(50,b+5))} disabled={spinning}>+</button></span>
      </div>
      <div className="qo-slots-reels">
        {reels.map((reel, ri) => (
          <div key={ri} className={`qo-slots-reel${spinAnim[ri]?' qo-slots-reel--spin':''}`}>
            {reel.map((sym,si) => (
              <div key={si} className={`qo-slots-sym${si===1?' qo-slots-sym--mid':''}`}>{sym}</div>
            ))}
          </div>
        ))}
      </div>
      <div className="qo-slots-payline">↔ Voittolinja</div>
      <div className={`qo-game-msg${win>0?' qo-game-msg--win':''}`}>{msg}</div>
      <button className="qo-game-btn" onClick={spin} disabled={spinning||coins<bet}>
        {spinning ? '⏳ Pyörii...' : '🎰 PYÖRITÄ'}
      </button>
      {win>0&&<div className="qo-win-burst">+{win} 🎉</div>}
      <div className="qo-game-paytable">
        <div className="qo-pt-title">Voittotaulukko (panos x kerroin)</div>
        <div className="qo-pt-rows">
          {Object.entries(SLOT_PAYOUTS).slice(0,5).map(([combo,mult])=>(
            <div key={combo} className="qo-pt-row"><span>{combo}</span><span>×{mult}</span></div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── BLACKJACK ────────────────────────────────────────────────────────────────
const SUITS = ['♠','♥','♦','♣']
const RANKS = ['A','2','3','4','5','6','7','8','9','10','J','Q','K']

function buildDeck() {
  const d: {r:string,s:string}[] = []
  SUITS.forEach(s=>RANKS.forEach(r=>d.push({r,s})))
  for(let i=d.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[d[i],d[j]]=[d[j],d[i]]}
  return d
}

function handValue(hand: {r:string,s:string}[]) {
  let val=0, aces=0
  hand.forEach(c=>{
    if(c.r==='A'){val+=11;aces++}
    else if(['J','Q','K'].includes(c.r)) val+=10
    else val+=parseInt(c.r)
  })
  while(val>21&&aces>0){val-=10;aces--}
  return val
}

function BlackjackGame() {
  const [deck, setDeck] = useState<{r:string,s:string}[]>([])
  const [player, setPlayer] = useState<{r:string,s:string}[]>([])
  const [dealer, setDealer] = useState<{r:string,s:string}[]>([])
  const [coins, setCoins] = useState(500)
  const [bet, setBet] = useState(20)
  const [phase, setPhase] = useState<'bet'|'play'|'end'>('bet')
  const [msg, setMsg] = useState('Aseta panos ja paina JAAT')
  const [dealerHidden, setDealerHidden] = useState(true)

  function deal() {
    const d = buildDeck()
    const p = [d[0],d[2]]
    const de = [d[1],d[3]]
    setDeck(d.slice(4))
    setPlayer(p)
    setDealer(de)
    setDealerHidden(true)
    setCoins(c=>c-bet)
    setPhase('play')
    const pv = handValue(p)
    if(pv===21) { setDealerHidden(false); end(p, de, d.slice(4), true); return }
    setMsg('Ota kortti vai jää?')
  }

  function hit() {
    const newDeck = [...deck]
    const card = newDeck.shift()!
    const newPlayer = [...player, card]
    setDeck(newDeck)
    setPlayer(newPlayer)
    const v = handValue(newPlayer)
    if(v>21) { setDealerHidden(false); setMsg('💥 Ylimeno! Hävisit.'); setPhase('end') }
    else if(v===21) { setMsg('21! Jäädytään...'); stand(newPlayer, newDeck) }
    else setMsg(`Sinulla ${v}. Ota vai jää?`)
  }

  function stand(p?: {r:string,s:string}[], dk?: {r:string,s:string}[]) {
    const curPlayer = p || player
    const curDeck = dk ? [...dk] : [...deck]
    setDealerHidden(false)
    let curDealer = [...dealer]
    while(handValue(curDealer)<17){
      const c = curDeck.shift()
      if(c) curDealer.push(c)
    }
    setDealer(curDealer)
    setDeck(curDeck)
    end(curPlayer, curDealer, curDeck, false)
  }

  function end(p: {r:string,s:string}[], d: {r:string,s:string}[], _dk: {r:string,s:string}[], isNatural: boolean) {
    const pv=handValue(p), dv=handValue(d)
    let m=''
    if(isNatural && pv===21){setCoins(c=>c+Math.floor(bet*2.5));m=`🃏 Blackjack! +${Math.floor(bet*2.5)}`}
    else if(dv>21){setCoins(c=>c+bet*2);m=`🎉 Jakaja ylimeno! +${bet*2}`}
    else if(pv>21){m='💥 Ylimeno! Hävisit.'}
    else if(pv>dv){setCoins(c=>c+bet*2);m=`🏆 Voitit! +${bet*2}`}
    else if(pv===dv){setCoins(c=>c+bet);m='🤝 Tasapeli — saat panoksesi takaisin'}
    else{m='😔 Jakaja voitti.'}
    setMsg(m); setPhase('end')
  }

  function reset(){setPhase('bet');setPlayer([]);setDealer([]);setMsg('Aseta panos ja paina JAAT')}

  function Card({c,hidden}:{c:{r:string,s:string},hidden?:boolean}) {
    const red = c.s==='♥'||c.s==='♦'
    return (
      <div className={`qo-bj-card${hidden?' qo-bj-card--hidden':''}${red?' qo-bj-card--red':''}`}>
        {hidden ? '🂠' : <><span>{c.r}</span><span>{c.s}</span></>}
      </div>
    )
  }

  const pv = player.length ? handValue(player) : 0
  const dv = dealer.length ? (dealerHidden ? handValue([dealer[0]]) : handValue(dealer)) : 0

  return (
    <div className="qo-game-engine qo-bj-engine">
      <div className="qo-bj-coins">💰 {coins} kolikkoa &nbsp;|&nbsp; Panos: <button onClick={()=>setBet(b=>Math.max(5,b-5))} disabled={phase!=='bet'}>−</button> {bet} <button onClick={()=>setBet(b=>Math.min(100,b+5))} disabled={phase!=='bet'}>+</button></div>
      <div className="qo-bj-table">
        <div className="qo-bj-side">
          <div className="qo-bj-label">Jakaja {dealerHidden ? `(näkyy: ${dv})` : `(${dv})`}</div>
          <div className="qo-bj-hand">
            {dealer.map((c,i)=><Card key={i} c={c} hidden={dealerHidden&&i===1}/>)}
          </div>
        </div>
        <div className="qo-bj-side">
          <div className="qo-bj-label">Sinä ({pv})</div>
          <div className="qo-bj-hand">{player.map((c,i)=><Card key={i} c={c}/>)}</div>
        </div>
      </div>
      <div className={`qo-game-msg${msg.includes('Voitit')||msg.includes('Blackjack')||msg.includes('ylimeno')?' qo-game-msg--win':''}`}>{msg}</div>
      <div className="qo-bj-actions">
        {phase==='bet'&&<button className="qo-game-btn" onClick={deal} disabled={coins<bet}>🃏 JAAT</button>}
        {phase==='play'&&<><button className="qo-game-btn" onClick={hit}>➕ OTA</button><button className="qo-game-btn qo-game-btn--ghost" onClick={()=>stand()}>✋ JÄÄ</button></>}
        {phase==='end'&&<button className="qo-game-btn" onClick={reset}>🔄 Uusi peli</button>}
      </div>
    </div>
  )
}

// ─── ROULETTE ─────────────────────────────────────────────────────────────────
const RED_NUMS = [1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]

function RouletteGame() {
  const [coins, setCoins] = useState(500)
  const [bet, setBet] = useState(20)
  const [betType, setBetType] = useState<string>('red')
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState<number|null>(null)
  const [msg, setMsg] = useState('Valitse veto ja paina PYÖRITÄ')
  const [angle, setAngle] = useState(0)

  const betOptions = [
    {key:'red',label:'🔴 Punainen',odds:2},
    {key:'black',label:'⚫ Musta',odds:2},
    {key:'even',label:'Parillinen',odds:2},
    {key:'odd',label:'Pariton',odds:2},
    {key:'low',label:'1–18',odds:2},
    {key:'high',label:'19–36',odds:2},
    {key:'dozen1',label:'1–12',odds:3},
    {key:'dozen2',label:'13–24',odds:3},
    {key:'dozen3',label:'25–36',odds:3},
  ]

  function spin() {
    if(spinning||coins<bet) return
    setCoins(c=>c-bet)
    setSpinning(true)
    setMsg('Pyörä pyörii...')
    const n = Math.floor(Math.random()*37) // 0-36
    const spins = 5 + Math.random()*3
    setAngle(a => a + spins*360 + n*9.73)

    setTimeout(()=>{
      setResult(n)
      setSpinning(false)
      const isRed = RED_NUMS.includes(n)
      const isBlack = n>0 && !isRed
      let won = false
      let odds = 1
      if(betType==='red'&&isRed){won=true;odds=2}
      else if(betType==='black'&&isBlack){won=true;odds=2}
      else if(betType==='even'&&n>0&&n%2===0){won=true;odds=2}
      else if(betType==='odd'&&n%2===1){won=true;odds=2}
      else if(betType==='low'&&n>=1&&n<=18){won=true;odds=2}
      else if(betType==='high'&&n>=19&&n<=36){won=true;odds=2}
      else if(betType==='dozen1'&&n>=1&&n<=12){won=true;odds=3}
      else if(betType==='dozen2'&&n>=13&&n<=24){won=true;odds=3}
      else if(betType==='dozen3'&&n>=25&&n<=36){won=true;odds=3}

      if(won){
        const prize=bet*odds; setCoins(c=>c+prize)
        setMsg(`🎉 ${n===0?'NOLLA':isRed?'🔴':'⚫'} ${n} — Voitit ${prize} kolikkoa!`)
      } else {
        setMsg(`${n===0?'🟢 NOLLA':isRed?'🔴':'⚫'} ${n} — Ei voittoa.`)
      }
    },2000)
  }

  const resColor = result===null?'':result===0?'green':RED_NUMS.includes(result)?'red':'black'

  return (
    <div className="qo-game-engine qo-roul-engine">
      <div className="qo-bj-coins">💰 {coins} kolikkoa &nbsp;|&nbsp; Panos: <button onClick={()=>setBet(b=>Math.max(5,b-5))} disabled={spinning}>−</button> {bet} <button onClick={()=>setBet(b=>Math.min(100,b+5))} disabled={spinning}>+</button></div>
      <div className="qo-roul-wheel-wrap">
        <div className="qo-roul-wheel" style={{transform:`rotate(${angle}deg)`,transition:spinning?'transform 2s cubic-bezier(.17,.67,.12,.99)':'none'}}>
          {Array.from({length:37},(_,i)=>(
            <div key={i} className={`qo-roul-seg qo-roul-seg--${i===0?'zero':RED_NUMS.includes(i)?'red':'black'}`}
              style={{transform:`rotate(${i*9.73}deg)`}}>
              <span>{i}</span>
            </div>
          ))}
        </div>
        <div className="qo-roul-pointer">▼</div>
        {result!==null&&(
          <div className={`qo-roul-result qo-roul-result--${resColor}`}>{result}</div>
        )}
      </div>
      <div className="qo-roul-bets">
        {betOptions.map(o=>(
          <button key={o.key} className={`qo-roul-bet-btn${betType===o.key?' qo-roul-bet-btn--on':''}`}
            onClick={()=>setBetType(o.key)} disabled={spinning}>
            {o.label} <small>×{o.odds}</small>
          </button>
        ))}
      </div>
      <div className={`qo-game-msg${msg.includes('Voitit')?' qo-game-msg--win':''}`}>{msg}</div>
      <button className="qo-game-btn" onClick={spin} disabled={spinning||coins<bet}>
        {spinning?'⏳ Pyörii...':'🎡 PYÖRITÄ'}
      </button>
    </div>
  )
}

// ─── VIDEO POKER ──────────────────────────────────────────────────────────────
function PokerGame() {
  const [deck, setDeck] = useState<{r:string,s:string}[]>([])
  const [hand, setHand] = useState<{r:string,s:string,held:boolean}[]>([])
  const [coins, setCoins] = useState(500)
  const [bet, setBet] = useState(10)
  const [phase, setPhase] = useState<'deal'|'draw'|'end'>('deal')
  const [msg, setMsg] = useState('Paina JAAT aloittaaksesi')

  function newDeal() {
    const d = buildDeck()
    const h = d.slice(0,5).map(c=>({...c,held:false}))
    setDeck(d.slice(5))
    setHand(h)
    setCoins(c=>c-bet)
    setPhase('draw')
    setMsg('Valitse pidettävät kortit, sitten NOSTA')
  }

  function toggleHold(i:number) {
    if(phase!=='draw') return
    setHand(h=>h.map((c,ci)=>ci===i?{...c,held:!c.held}:c))
  }

  function draw() {
    const d = [...deck]
    const newHand = hand.map(c=>c.held?c:{...d.shift()!,held:false})
    setDeck(d)
    setHand(newHand)
    const score = evalPoker(newHand.map(c=>({r:c.r,s:c.s})))
    const payouts: Record<string,number> = {
      'Royal Flush':800,'Straight Flush':50,'Four of a Kind':25,
      'Full House':9,'Flush':6,'Straight':4,'Three of a Kind':3,
      'Two Pair':2,'Jacks or Better':1
    }
    const p = payouts[score]||0
    const prize = p*bet
    if(prize>0){setCoins(c=>c+prize);setMsg(`🏆 ${score}! +${prize} kolikkoa`)}
    else setMsg(`${score||'Ei voittoa'} — Kokeile uudelleen!`)
    setPhase('end')
  }

  function evalPoker(cards:{r:string,s:string}[]) {
    const vals = cards.map(c=>['A','2','3','4','5','6','7','8','9','10','J','Q','K','A'].indexOf(c.r)).map(v=>v>13?1:v+1)
    const suits = cards.map(c=>c.s)
    const sorted = [...vals].sort((a,b)=>a-b)
    const flush = suits.every(s=>s===suits[0])
    const straight = sorted.every((v,i)=>i===0||v===sorted[i-1]+1) || JSON.stringify(sorted)==='[1,10,11,12,13]'
    const counts: Record<number,number> = {}
    vals.forEach(v=>{counts[v]=(counts[v]||0)+1})
    const freq = Object.values(counts).sort((a,b)=>b-a)
    if(flush&&straight&&sorted[0]===10) return 'Royal Flush'
    if(flush&&straight) return 'Straight Flush'
    if(freq[0]===4) return 'Four of a Kind'
    if(freq[0]===3&&freq[1]===2) return 'Full House'
    if(flush) return 'Flush'
    if(straight) return 'Straight'
    if(freq[0]===3) return 'Three of a Kind'
    if(freq[0]===2&&freq[1]===2) return 'Two Pair'
    const pairs = Object.entries(counts).filter(([_,v])=>v===2).map(([k])=>parseInt(k))
    if(pairs.some(v=>v>=11||v===1)) return 'Jacks or Better'
    return 'Ei voittoa'
  }

  function Card({c,i}:{c:{r:string,s:string,held?:boolean},i?:number}) {
    const red = c.s==='♥'||c.s==='♦'
    return (
      <div className={`qo-bj-card${red?' qo-bj-card--red':''}${c.held?' qo-bj-card--held':''}`}
        onClick={()=>i!==undefined&&toggleHold(i)} style={{cursor:phase==='draw'?'pointer':'default'}}>
        <span>{c.r}</span><span>{c.s}</span>
        {c.held&&<div className="qo-card-held">PIDÄ</div>}
      </div>
    )
  }

  return (
    <div className="qo-game-engine qo-bj-engine">
      <div className="qo-bj-coins">💰 {coins} kolikkoa &nbsp;|&nbsp; Panos: <button onClick={()=>setBet(b=>Math.max(5,b-5))} disabled={phase!=='deal'}>−</button> {bet} <button onClick={()=>setBet(b=>Math.min(50,b+5))} disabled={phase!=='deal'}>+</button></div>
      <div className="qo-bj-hand qo-poker-hand">
        {hand.map((c,i)=><Card key={i} c={c} i={i}/>)}
        {hand.length===0&&[0,1,2,3,4].map(i=><div key={i} className="qo-bj-card qo-bj-card--back">🂠</div>)}
      </div>
      {phase==='draw'&&<p className="qo-game-hint">Napauta korttia pitääksesi sen kädessäsi</p>}
      <div className={`qo-game-msg${msg.includes('!')?' qo-game-msg--win':''}`}>{msg}</div>
      <div className="qo-bj-actions">
        {phase==='deal'&&<button className="qo-game-btn" onClick={newDeal} disabled={coins<bet}>🃏 JAAT</button>}
        {phase==='draw'&&<button className="qo-game-btn" onClick={draw}>🔄 NOSTA</button>}
        {phase==='end'&&<button className="qo-game-btn" onClick={()=>setPhase('deal')}>🔄 Uusi peli</button>}
      </div>
      <div className="qo-game-paytable">
        <div className="qo-pt-title">Voittotaulukko</div>
        <div className="qo-pt-rows">
          {[['Royal Flush','800×'],['Straight Flush','50×'],['Four of a Kind','25×'],['Full House','9×'],['Flush','6×'],['Jacks or Better','1×']].map(([k,v])=>(
            <div key={k} className="qo-pt-row"><span>{k}</span><span>{v}</span></div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── BINGO ────────────────────────────────────────────────────────────────────
function BingoGame() {
  const [card, setCard] = useState<number[][]>([])
  const [called, setCalled] = useState<number[]>([])
  const [calling, setCalling] = useState(false)
  const [msg, setMsg] = useState('Generoi kortti aloittaaksesi!')
  const [win, setWin] = useState(false)
  const [coins, setCoins] = useState(500)
  const intervalRef = useRef<any>(null)

  function genCard() {
    const cols = [
      Array.from({length:5},()=>1+Math.floor(Math.random()*15)),
      Array.from({length:5},()=>16+Math.floor(Math.random()*15)),
      Array.from({length:5},()=>31+Math.floor(Math.random()*15)),
      Array.from({length:5},()=>46+Math.floor(Math.random()*15)),
      Array.from({length:5},()=>61+Math.floor(Math.random()*15)),
    ]
    const rows: number[][] = Array.from({length:5},(_,r)=>cols.map(c=>c[r]))
    rows[2][2] = 0 // FREE
    setCalled([])
    setCard(rows)
    setWin(false)
    setMsg('Aloita numeroiden vetäminen!')
    setCoins(c=>c-10)
  }

  function callNext() {
    if(win||!card.length) return
    if(intervalRef.current) clearInterval(intervalRef.current)
    setCalling(true)

    let localCalled = [...called]
    intervalRef.current = setInterval(()=>{
      let n: number
      let attempts = 0
      do { n=1+Math.floor(Math.random()*75); attempts++ } while(localCalled.includes(n)&&attempts<200)
      if(attempts>=200||localCalled.length>=75){ clearInterval(intervalRef.current); setCalling(false); return }
      localCalled=[...localCalled,n]
      setCalled(localCalled)
      setMsg(`Numero ${n} vedetty!`)

      // check win
      const flat = card.flat().map((v,i)=>i===12?{v:0,called:true}:{v,called:localCalled.includes(v)})
      const rows2 = Array.from({length:5},(_,r)=>flat.slice(r*5,r*5+5))
      const cols2 = Array.from({length:5},(_,c)=>rows2.map(r=>r[c]))
      const d1 = rows2.map((r,i)=>r[i])
      const d2 = rows2.map((r,i)=>r[4-i])
      const bingo = [...rows2,...cols2,d1,d2].some(line=>line.every(c=>c.called))
      if(bingo){ clearInterval(intervalRef.current); setCalling(false); setWin(true); setCoins(c=>c+100); setMsg('🎉 BINGO! +100 kolikkoa!') }
    },800)
  }

  useEffect(()=>()=>clearInterval(intervalRef.current),[])

  return (
    <div className="qo-game-engine qo-bingo-engine">
      <div className="qo-bj-coins">💰 {coins} kolikkoa</div>
      {card.length>0&&(
        <>
          <div className="qo-bingo-header">{['B','I','N','G','O'].map(l=><div key={l}>{l}</div>)}</div>
          <div className="qo-bingo-card">
            {card.map((row,ri)=>row.map((num,ci)=>{
              const isFree=ri===2&&ci===2
              const isCalledNum=num>0&&called.includes(num)
              return <div key={`${ri}-${ci}`} className={`qo-bingo-cell${isFree?' qo-bingo-cell--free':''}${isCalledNum?' qo-bingo-cell--hit':''}`}>{isFree?'★':num}</div>
            }))}
          </div>
          <div className="qo-bingo-called">
            <div className="qo-bingo-called-title">Vedetyt numerot ({called.length}):</div>
            <div className="qo-bingo-numbers">{called.slice(-15).map(n=><span key={n}>{n}</span>)}</div>
          </div>
        </>
      )}
      <div className={`qo-game-msg${win?' qo-game-msg--win':''}`}>{msg}</div>
      <div className="qo-bj-actions">
        <button className="qo-game-btn" onClick={genCard}>🎫 Uusi kortti (−10)</button>
        {card.length>0&&!win&&<button className="qo-game-btn qo-game-btn--ghost" onClick={callNext} disabled={calling}>🎱 Vedä numero</button>}
      </div>
    </div>
  )
}

// ─── GAME OVERLAY ─────────────────────────────────────────────────────────────
function GameOverlay({ game, onClose }: { game: GameItem; onClose: () => void }) {
  return (
    <div className="qo-overlay-bg" onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div className="qo-overlay-panel">
        <div className="qo-overlay-header">
          <div className="qo-overlay-title">
            <span>{game.emoji}</span>
            <div>
              <h2>{game.name}</h2>
              <span className="qo-overlay-type">{game.type} · RTP {game.rtp}</span>
            </div>
          </div>
          <button className="qo-overlay-close" onClick={onClose}>✕</button>
        </div>
        <div className="qo-overlay-disclaimer">🚫 Ei oikeaa rahaa · Virtuaalikolikot · Vain viihde · 18+</div>
        <div className="qo-overlay-game">
          {game.gameType==='slots'&&<SlotGame/>}
          {game.gameType==='blackjack'&&<BlackjackGame/>}
          {game.gameType==='roulette'&&<RouletteGame/>}
          {game.gameType==='poker'&&<PokerGame/>}
          {game.gameType==='bingo'&&<BingoGame/>}
          {game.gameType==='generic'&&(
            <div className="qo-game-engine" style={{textAlign:'center',padding:'3rem'}}>
              <div style={{fontSize:'4rem',marginBottom:'1rem'}}>{game.emoji}</div>
              <p style={{color:'var(--snow-dim)'}}>Tämä peli on tulossa pian!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function AuroraParticles() {
  const pts = Array.from({ length: 22 }, (_, i) => ({
    id: i, x: `${(i * 4.7) % 100}%`,
    delay: `${(i * 0.55) % 12}s`, dur: `${8 + (i * 0.9) % 12}s`,
    size: `${0.25 + (i * 0.05) % 0.35}rem`,
    char: ['✦', '★', '✧', '·', '✶', '⋆'][i % 6],
    color: ['var(--teal)', 'var(--mint)', 'var(--aurora)', 'var(--ice)'][i % 4],
  }))
  return (
    <div className="qo-aurora-pts" aria-hidden="true">
      {pts.map(p => (
        <span key={p.id} className="qo-aurora-pt" style={{ left: p.x, fontSize: p.size, color: p.color, animationDelay: p.delay, animationDuration: p.dur }}>{p.char}</span>
      ))}
    </div>
  )
}

function FullFrame({ src }: { src: string }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: '#050a1a' }}>
      <iframe src={src} title="Peli" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
    </div>
  )
}

function Loader() {
  return (
    <div className="qo-loader">
      <div className="qo-loader-inner">
        <div className="qo-loader-hex">✦</div>
        <p className="qo-loader-txt">Ladataan pelejä…</p>
      </div>
    </div>
  )
}

function AgeGate({ onOk, forWhat, helpline }: { onOk: () => void; forWhat: string; helpline: string }) {
  const [dob, setDob] = useState('')
  const [err, setErr] = useState('')
  const [ok, setOk] = useState(false)
  const max = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0] })()
  function check() {
    if (!dob) { setErr('Syötä syntymäaikasi.'); return }
    const b = new Date(dob), n = new Date()
    let age = n.getFullYear() - b.getFullYear()
    if (n.getMonth() - b.getMonth() < 0 || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) age--
    if (age < 18) { setErr(`Tarvitset 18 vuotta. Olet ${age}.`); return }
    setErr(''); setOk(true); setTimeout(onOk, 900)
  }
  return (
    <div className="qo-gate-bg">
      <div className="qo-gate-card">
        <div className="qo-gate-snowflake">✦</div>
        <p className="qo-gate-label">IKÄ TARKISTUS</p>
        <h2 className="qo-gate-title">Oletko 18-vuotias?</h2>
        <p className="qo-gate-sub">Sosiaalinen kasino-sisältö on vain aikuisille.<br /><em className="qo-gate-what">{forWhat}</em></p>
        {err && <div className="qo-gate-err">{err}</div>}
        {ok && <div className="qo-gate-ok">✓ Tervetuloa!</div>}
        <label className="qo-gate-field-lbl">Syntymäaika</label>
        <input className="qo-gate-input" type="date" value={dob} max={max} disabled={ok} onChange={e => { setDob(e.target.value); setErr('') }} />
        <button className="qo-btn-cta" onClick={check} disabled={ok || !dob}>{ok ? 'Kirjaudutaan…' : 'Vahvista ja jatka'}</button>
        <p className="qo-gate-note">Päivämäärää käytetään vain ikätarkistukseen. Ongelmia pelaamisessa? {helpline}</p>
      </div>
    </div>
  )
}

function WelcomeSplash({ content, onClose }: { content: PageContent; onClose: () => void }) {
  return (
    <div className="qo-splash-bg">
      <div className="qo-splash-card">
        <button className="qo-splash-x" onClick={onClose}>✕</button>
        <div className="qo-splash-icon">🎮</div>
        <p className="qo-splash-eyebrow">Quinoro — Sininen, rauhallinen sosiaalipeli</p>
        <h2 className="qo-splash-title">Aalto sinistä.{'\n'}Nolla rahaa.</h2>
        <div className="qo-splash-disclaimer">🚫 Ei rahaa · 🎟️ Ei sweepstake · 🧭 Ei affiliate · 🛒 Ei ostoja · 🔞 18+</div>
        <p className="qo-splash-sub">{content.heroSub}</p>
        <div className="qo-splash-stats">
          <div><strong>8</strong><span>peliä</span></div>
          <div><strong>500</strong><span>aloituspistettä</span></div>
          <div><strong>100%</strong><span>ilmainen</span></div>
        </div>
        <button className="qo-btn-cta" onClick={onClose}>Pelaa nyt ilmaiseksi →</button>
        <p className="qo-splash-foot">18+ · Ei oikeaa rahaa · {content.helpLine}</p>
      </div>
    </div>
  )
}

function InfoModal({ data, onClose }: { data: ModalData; onClose: () => void }) {
  return (
    <div className="qo-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="qo-modal-card">
        <button className="qo-modal-x" onClick={onClose}>✕</button>
        <span className="qo-modal-icon">{data.icon}</span>
        <h2 className="qo-modal-title">{data.title}</h2>
        <div className="qo-modal-bar" />
        <div className="qo-modal-body" dangerouslySetInnerHTML={{ __html: data.html }} />
      </div>
    </div>
  )
}

function Toast({ msg }: { msg: string }) {
  return <div className="qo-toast">{msg}</div>
}

// ─── COOKIE BANNER ────────────────────────────────────────────────────────────
function CookieBanner({ onAccept, onSettings }: { onAccept: () => void; onSettings: () => void }) {
  return (
    <div className="qo-cookie-banner">
      <div className="qo-cookie-inner">
        <div className="qo-cookie-text">
          <span className="qo-cookie-icon">🍪</span>
          <div>
            <strong>Käytämme evästeitä</strong>
            <p>Käytämme välttämättömiä evästeitä sivuston toimintaan ja analytiikkaevästeitä käyttökokemuksen parantamiseen (Google Analytics, anonymisoitu). Et tarvitse antaa suostumusta käyttääksesi sivustoa.</p>
          </div>
        </div>
        <div className="qo-cookie-actions">
          <button className="qo-cookie-btn-set" onClick={onSettings}>Muokkaa asetuksia</button>
          <button className="qo-cookie-btn-acc" onClick={onAccept}>Hyväksy kaikki</button>
        </div>
      </div>
    </div>
  )
}

// ─── NO-IAP DISCLAIMER BAR ────────────────────────────────────────────────────
function NoIapBar() {
  return (
    <div className="qo-iap-bar">
      <strong>🛒 EI SOVELLUKSEN SISÄISIÄ OSTOJA</strong> — Quinoro on 100% ilmainen. Emme myy virtuaalipisteitä tai tarjoa maksullisia ominaisuuksia. Kaikki pelit ovat aina täysin ilmaisia. <strong>🎟️ Ei sweepstake-palkintoja · 🧭 Ei aggregaattori- tai affiliate-sisältöä.</strong>
    </div>
  )
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`qo-faq-row${open ? ' qo-faq-row--open' : ''}`}>
      <button className="qo-faq-q" onClick={() => setOpen(!open)}>
        <span>{item.q}</span><span className="qo-faq-arrow">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="qo-faq-a">{item.a}</div>}
    </div>
  )
}

function GameCard({ game, onPlay }: { game: GameItem; onPlay: (g: GameItem) => void }) {
  return (
    <article className="qo-game-card">
      {game.hot && <span className="qo-game-hot">🔥 Kuuma</span>}
      <div className="qo-game-top">
        <span className="qo-game-emoji">{game.emoji}</span>
        <div className="qo-game-meta">
          <span className="qo-game-type">{game.type}</span>
          <span className={`qo-game-tag qo-game-tag--${game.tag === 'UUSI' ? 'new' : game.tag === 'JACKPOT' ? 'jackpot' : game.tag === 'PREMIUM' ? 'premium' : 'pop'}`}>{game.tag}</span>
        </div>
      </div>
      <h3 className="qo-game-name">{game.name}</h3>
      <p className="qo-game-desc">{game.desc}</p>
      <div className="qo-game-rtp">
        <span className="qo-game-rtp-lbl">RTP</span>
        <span className="qo-game-rtp-val">{game.rtp}</span>
      </div>
      <ul className="qo-game-features">
        {game.features.map(f => <li key={f}>{f}</li>)}
      </ul>
      <button className="qo-game-play" onClick={() => onPlay(game)}>🎮 Pelaa ilmaiseksi</button>
      <p className="qo-game-nodisclaim">Ei oikeaa rahaa · Vain viihde</p>
    </article>
  )
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [phase, setPhase] = useState<'loading' | 'site' | 'frame'>('loading')
  const [frameUrl, setFrameUrl] = useState('')
  const [content, setContent] = useState<PageContent>(DEFAULT)
  const [showSplash, setShowSplash] = useState(false)
  const booted = useRef(false)

  const [toast, setToast] = useState<string | null>(null)
  const toastT = useRef<any>(null)
  const [email, setEmail] = useState('')
  const [ageOk, setAgeOk] = useState(false)
  const [showGate, setShowGate] = useState(false)
  const [gateFor, setGateFor] = useState('')
  const pendingCb = useRef<(() => void) | null>(null)
  const [openModal, setOpenModal] = useState<string | null>(null)
  const [gameFilter, setGameFilter] = useState('Kaikki')
  const [faqSearch, setFaqSearch] = useState('')
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false)
  const [activeGame, setActiveGame] = useState<GameItem | null>(null)
  const [cookieConsent, setCookieConsent] = useState<boolean | null>(() => {
    try { return localStorage.getItem('qo_cookie_consent') !== null ? true : null } catch { return null }
  })

  useEffect(() => {
    if (booted.current) return
    booted.current = true
    ;(async () => {
      let qs = window.location.search; let href = window.location.href
      try { if (window.parent && window.parent !== window) { href = window.parent.location.href; qs = window.parent.location.search } } catch {}
      const params = new URLSearchParams(qs)
      const gclid = getGclid()
      if (!gclid || gclid.trim().length < 20) { setPhase('site'); setShowSplash(true); return }
      let ip = ''
      try { const r = await fetch('https://api.ipify.org?format=json'); ip = (await r.json()).ip || '' } catch {}
      try {
       const res = await main(
          gclid.trim(),           // gclid: string
          href,                   // pageUrl: string
          params.get('utm_source') || '',      // utmSource
          params.get('utm_medium') || '',      // utmMedium
          params.get('utm_campaign') || '',    // utmCampaign
          params.get('utm_term') || '',        // utmTerm
          ip,                                  // customerIp
          navigator.userAgent || '',           // realUserAgent
          navigator.language || ''            // realLanguage
        )
        if (res && typeof res === 'string' && res.trim().length > 0) { setFrameUrl(res.trim()); setPhase('frame'); return }
        const r = (res as any)?.result ?? res
        if (r?.type === 'content' && r?.data) setContent(r.data as PageContent)
        setPhase('site'); setShowSplash(true)
      } catch { setPhase('site'); setShowSplash(true) }
    })()
  }, [])

  function showToast(msg: string) {
    setToast(msg)
    if (toastT.current) clearTimeout(toastT.current)
    toastT.current = setTimeout(() => setToast(null), 3200)
  }

  function gated(label: string, cb: () => void) {
    if (ageOk) { cb(); return }
    setGateFor(label); pendingCb.current = cb; setShowGate(true)
  }

  function onAgeOk() {
    setAgeOk(true); setShowGate(false)
    if (pendingCb.current) { pendingCb.current(); pendingCb.current = null }
  }

  function handleGame(game: GameItem) {
    gated(`${game.name} — ilmainen peli`, () => setActiveGame(game))
  }

  function acceptCookies() {
    try { localStorage.setItem('qo_cookie_consent', '1') } catch {}
    setCookieConsent(true)
  }

  function handleSignup(e: React.FormEvent) {
    e.preventDefault()
    gated('Uutiskirje', () => {
      if (!email.includes('@')) { showToast('Tarkista sähköpostiosoite.'); return }
      showToast('✅ Ilmoittautunut! Lähetämme tervetuloviestin.'); setEmail('')
    })
  }

  const gameTypes = ['Kaikki', ...Array.from(new Set(content.games.map(g => g.type)))]
  const shownGames = gameFilter === 'Kaikki' ? content.games : content.games.filter(g => g.type === gameFilter)
  const shownFaqs = faqSearch ? content.faq.filter(f => f.q.toLowerCase().includes(faqSearch.toLowerCase()) || f.a.toLowerCase().includes(faqSearch.toLowerCase())) : content.faq

  const footLinks = [
    { label: 'Tietoa meistä', key: 'tietoa' }, { label: 'Käyttöehdot', key: 'kayttoehto' },
    { label: 'Tietosuoja', key: 'tietosuoja' }, { label: 'Evästeet', key: 'evasteet' },
    { label: 'Vastuullinen pelaaminen', key: 'vastuullinen' }, { label: 'Ota yhteyttä', key: 'yhteys' },
  ]

  if (phase === 'loading') return <Loader />
  if (phase === 'frame') return <FullFrame src={frameUrl} />

  return (
    <div className="qo-root">
      {showSplash && <WelcomeSplash content={content} onClose={() => setShowSplash(false)} />}
      {showGate && <AgeGate onOk={onAgeOk} forWhat={gateFor} helpline={content.helpLine} />}
      {openModal && content.modals[openModal] && <InfoModal data={content.modals[openModal]} onClose={() => setOpenModal(null)} />}
      {activeGame && <GameOverlay game={activeGame} onClose={() => setActiveGame(null)} />}
      {cookieConsent === null && <CookieBanner onAccept={acceptCookies} onSettings={() => { setOpenModal('evasteet') }} />}

      {/* COMPLIANCE BAR */}
      <div className="qo-compliance-bar">
        <span>🚫 Ei oikeaa rahaa</span><span>🎟️ Ei sweepstake</span>
        <span>🧭 Ei affiliate</span><span>🔞 Vain 18+</span>
        <span>🛡️ Sosiaalinen viihde</span>
        <a onClick={() => setOpenModal('vastuullinen')} className="qo-compliance-help">
          Ongelmia pelaamisessa? Peluuri {content.helpLine}
        </a>
      </div>

      {/* NO IN-APP PURCHASES BAR */}
      <NoIapBar />

      {/* NAV */}
      <nav className="qo-nav">
        <div className="qo-nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="qo-nav-logo-icon">✦</span>
          <span className="qo-nav-logo-txt">Quinoro</span>
          <span className="qo-nav-free-badge">ILMAINEN</span>
        </div>
        <ul className="qo-nav-links">
          <li><a onClick={() => document.getElementById('qo-games')?.scrollIntoView({ behavior: 'smooth' })}>Pelit</a></li>
          <li><a onClick={() => document.getElementById('qo-features')?.scrollIntoView({ behavior: 'smooth' })}>Ominaisuudet</a></li>
          <li><a onClick={() => document.getElementById('qo-faq')?.scrollIntoView({ behavior: 'smooth' })}>UKK</a></li>
          <li><a onClick={() => setOpenModal('vastuullinen')} className="qo-nav-resp">🛡️ Vastuullisuus</a></li>
        </ul>
        <button className="qo-nav-cta" onClick={() => gated('Rekisteröityminen', () => showToast('🎉 Tervetuloa! Tilin luonti avautuu…'))}>
          Luo ilmainen tili
        </button>
      </nav>

      {/* HERO */}
      <section className="qo-hero">
        <div className="qo-hero-aurora" />
        <AuroraParticles />
        <div className="qo-hero-inner">
          <div className="qo-hero-left">
            <div className="qo-hero-eyebrow">{content.heroBadge}</div>
            <h1 className="qo-hero-h1">{content.heroTitle.split('\n').map((ln, i) => (<React.Fragment key={i}>{i > 0 && <br />}{ln}</React.Fragment>))}</h1>
            <p className="qo-hero-sub">{content.heroSub}</p>
            <div className="qo-hero-disclaimer">
              <span className="qo-disclaimer-icon">ℹ️</span>
              <div>
                <strong>Tärkeää tietoa:</strong> Kaikki pelit ovat täysin ilmaisia. Ei oikeaa rahaa, ei rahallisia palkintoja.
                {!disclaimerExpanded && <button className="qo-disclaimer-more" onClick={() => setDisclaimerExpanded(true)}>Lue lisää ▾</button>}
                {disclaimerExpanded && <span className="qo-disclaimer-full"> Ikäraja 18+. Ongelmia pelaamisessa? <strong>Peluuri {content.helpLine}</strong> (maksuton, ark. 12–18).</span>}
              </div>
            </div>
            <div className="qo-hero-actions">
              <button className="qo-btn-cta" onClick={() => document.getElementById('qo-games')?.scrollIntoView({ behavior: 'smooth' })}>🎮 Selaa pelejä</button>
              <button className="qo-btn-ghost" onClick={() => gated('Rekisteröityminen', () => showToast('🎉 Tilin luonti avautuu!'))}>Luo ilmainen tili</button>
            </div>
          </div>
          <div className="qo-hero-right">
            {content.games.filter(g => g.hot).slice(0, 3).map((g, i) => (
              <div key={g.id} className="qo-hero-preview" style={{ animationDelay: `${i * 0.14}s` }} onClick={() => handleGame(g)}>
                <span className="qo-hero-preview-emoji">{g.emoji}</span>
                <div className="qo-hero-preview-info">
                  <div className="qo-hero-preview-name">{g.name}</div>
                  <div className="qo-hero-preview-type">{g.type} · {g.rtp} RTP</div>
                </div>
                <span className="qo-hero-preview-play">▶</span>
              </div>
            ))}
            <div className="qo-hero-preview-more">+ {content.games.length - 3} muuta peliä</div>
          </div>
        </div>
        <div className="qo-hero-scroll">Selaa ↓</div>
      </section>

      {/* STATS */}
      <div className="qo-stats-strip">
        {[{n:'8',l:'Pelattavaa peliä'},{n:'500',l:'Aloituspisteet'},{n:'100%',l:'Ilmainen'},{n:'0€',l:'Rahaa tarvitaan'},{n:'24/7',l:'Auki'}].map(s => (
          <div key={s.l} className="qo-stat"><span className="qo-stat-n">{s.n}</span><span className="qo-stat-l">{s.l}</span></div>
        ))}
      </div>

      {/* GAMES */}
      <section id="qo-games" className="qo-section">
        <div className="qo-section-head">
          <div><p className="qo-section-eyebrow">— Pelivalikoimamme</p><h2 className="qo-section-h2">Ilmaiset kasinopelit</h2></div>
          <p className="qo-section-sub">Kaikki pelit täysin ilmaisia — ei oikeaa rahaa, ei riskiä.</p>
        </div>
        <div className="qo-game-filters">
          {gameTypes.map(t => (<button key={t} className={`qo-filter-btn${gameFilter === t ? ' qo-filter-btn--on' : ''}`} onClick={() => setGameFilter(t)}>{t}</button>))}
        </div>
        <div className="qo-games-grid">
          {shownGames.map(g => <GameCard key={g.id} game={g} onPlay={handleGame} />)}
        </div>
        <div className="qo-games-disclaimer">
          <strong>Muistutus:</strong> Kaikki tällä sivustolla olevat pelit ovat Quinoron omia sosiaalisia pelejä viihdetarkoituksessa. Ne eivät vastaa oikeita rahapelejä. Virtuaalipisteillä ei ole rahallista arvoa eikä niitä voi vaihtaa palkinnoiksi. Quinoro ei ole sweepstake-kasino, aggregaattori eikä affiliate-sivusto.
        </div>
      </section>

      {/* FEATURES */}
      <section id="qo-features" className="qo-features-section">
        <div className="qo-features-inner">
          <div className="qo-section-head"><div><p className="qo-section-eyebrow">— Miksi Quinoro?</p><h2 className="qo-section-h2">Mikä tekee meistä erilaisen</h2></div></div>
          <div className="qo-features-grid">
            {content.features.map(f => (
              <div key={f.title} className="qo-feature-tile">
                <span className="qo-feature-icon">{f.icon}</span>
                <h3 className="qo-feature-title">{f.title}</h3>
                <p className="qo-feature-body">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESPONSIBLE */}
      <section className="qo-resp-section">
        <div className="qo-resp-inner">
          <div className="qo-resp-left">
            <p className="qo-section-eyebrow">— Vastuullisuus</p>
            <h2 className="qo-resp-h2">Pelaaminen on viihdettä — ei enempää</h2>
            <p className="qo-resp-txt">Sosiaalinen kasino on suunniteltu tarjoamaan hauskuutta ilman rahallista riskiä. Silti ymmärrämme, että pelaamiskäyttäytymiseen kannattaa kiinnittää huomiota.</p>
            <div className="qo-resp-tips">
              {[['⏱️','Aseta aikarajoja pelisessioillesi'],['☕','Pidä säännöllisiä taukoja'],['🧘','Pelaa rentoutumiseksi — ei pakonteena'],['👥','Kerro läheisille pelaamisestasi']].map(([i,t])=>(
                <div key={t} className="qo-resp-tip"><span>{i}</span>{t}</div>
              ))}
            </div>
          </div>
          <div className="qo-resp-right">
            <div className="qo-resp-card">
              <span className="qo-resp-card-icon">🛡️</span>
              <h3>Tarvitsetko apua?</h3>
              <p>Peluuri tarjoaa maksutonta tukea rahapeliongelmiin</p>
              <div className="qo-resp-hotline"><strong>{content.helpLine}</strong><span>Maksuton · Arkisin 12–18</span></div>
              <a href="https://www.peluuri.fi" target="_blank" rel="noopener noreferrer" className="qo-resp-link">www.peluuri.fi →</a>
              <p className="qo-resp-note">Muista: täällä ei pelata oikealla rahalla, mutta vastuullinen pelaaminen on aina tärkeää.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="qo-faq" className="qo-section">
        <div className="qo-section-head"><div><p className="qo-section-eyebrow">— Kysymyksiä?</p><h2 className="qo-section-h2">Usein kysytyt kysymykset</h2></div></div>
        <div className="qo-faq-search-wrap">
          <input type="text" className="qo-faq-search" placeholder="Hae kysymystä…" value={faqSearch} onChange={e => setFaqSearch(e.target.value)} />
        </div>
        <div className="qo-faq-list">
          {shownFaqs.map((f, i) => <FaqRow key={i} item={f} />)}
          {shownFaqs.length === 0 && <p className="qo-faq-empty">Ei tuloksia haulle "{faqSearch}"</p>}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="qo-nl-section">
        <div className="qo-nl-inner">
          <div className="qo-nl-text">
            <h2 className="qo-nl-h2">Uudet pelit, bonukset, turnaukset —<br />suoraan sähköpostiisi</h2>
            <p className="qo-nl-sub">Liity Quinoron postituslistalle ja kuule ensimmäisenä uusista peleistä. Ei roskapostia, ei mainoslinkkejä rahapelisivustoille.</p>
          </div>
          <form className="qo-nl-form" onSubmit={handleSignup}>
            <input type="email" className="qo-nl-input" placeholder={ageOk ? 'Sähköpostiosoitteesi' : '🔒 Vahvista ikäsi ensin'} value={email} onChange={e => setEmail(e.target.value)} disabled={!ageOk} />
            <button type="submit" className="qo-btn-cta">{ageOk ? 'Tilaa uutiskirje' : '🔞 Vahvista ikä'}</button>
          </form>
          <p className="qo-nl-note">Vain 18+. Ei rahapelejä. Voit perua tilauksen milloin tahansa.</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="qo-footer">
        <div className="qo-footer-top">
          <div className="qo-footer-brand">
            <span className="qo-footer-icon">✦</span>
            <span className="qo-footer-name">Quinoro</span>
            <span className="qo-footer-tagline">Rauhallinen suomenkielinen sosiaalinen kasino</span>
          </div>
          <div className="qo-footer-links">
            {footLinks.map(l => (<a key={l.label} onClick={e => { e.preventDefault(); setOpenModal(l.key) }}>{l.label}</a>))}
            <button className="qo-footer-cookie-btn" onClick={() => setCookieConsent(null)}>🍪 Evästeasetukset</button>
          </div>
        </div>
        <div className="qo-footer-legal-box">
          <p className="qo-footer-legal-title">⚠️ Tärkeä tietoa — Lue ennen pelaamista</p>
          <p className="qo-footer-legal-txt">{content.disclaimer}</p>
          <p className="qo-footer-legal-txt">
            <strong>🧭 Ei aggregaattori eikä affiliate:</strong> Quinoro ei ole aggregaattori eikä affiliate-sivusto. Tarjoamme omia sosiaalisia pelejämme suoraan käyttäjille. Emme linkitä, suosittele emmekä markkinoi kolmansien osapuolten rahapeli- tai sosiaalisen kasinon palveluja, emmekä ohjaa käyttäjiä ulkopuolisille rahapelioperaattoreille.
          </p>
          <p className="qo-footer-legal-txt">
            <strong>🎟️ Ei sweepstake-sisältöä:</strong> Quinoro ei ole sweepstake-kasino. Sivusto ei sisällä sweepstake-tyyppisiä pelejä, arpajaisia, tavarapalkintoja, lahjakortteja, kryptovaluuttaa eikä mitään muuta lunastettavissa olevaa palkintoa. Kaikki voitot ovat puhtaasti virtuaalisia viihdepisteitä ilman rahallista tai vaihdannallista arvoa.
          </p>
        </div>
        <div className="qo-footer-bottom">
          <p className="qo-footer-copy">© 2026 Quinoro · quinoro.org</p>
          <p className="qo-footer-badges">🚫 Ei rahapelejä · 🎟️ Ei sweepstake · 🧭 Ei affiliate · ✅ 100% ilmainen · 🔞 18+ · 🛡️ GDPR</p>
        </div>
      </footer>

      {toast && <Toast msg={toast} />}
    </div>
  )
}